import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { responseInitialValues } from "@utils/response"
import { ResponseDefault, ResponsePattern } from "@utils/response/types"
import { Convite, IGeoLocalizacao } from "../interfaces/Convite"
import { CampoDinamico } from "../interfaces/CampoDinamico"
import { LivenessResult } from "../interfaces/LivenessResult"

interface State {
    requestGetConvite: ResponseDefault<Convite>
    requestGetConviteCampos: ResponseDefault<CampoDinamico[]>
    requestSaveConvite: ResponseDefault<any>
    requestGetLocalizacao: ResponseDefault<IGeoLocalizacao>
    requestGetSessionId: ResponseDefault<any>
    requestValidadeLiveness: ResponseDefault<LivenessResult>
}

const initialState: State = {
    requestGetConvite: responseInitialValues,
    requestGetConviteCampos: responseInitialValues,
    requestSaveConvite: responseInitialValues,
    requestGetLocalizacao: responseInitialValues,
    requestGetSessionId: responseInitialValues,
    requestValidadeLiveness: responseInitialValues,
}

const requestConviteSlice = createSlice({
    name: 'requestConvite',
    initialState,
    reducers: {
        setRequestGetConviteData: (state: State, action: PayloadAction<ResponsePattern<Convite>>) => {
            state.requestGetConvite.data = action.payload
            state.requestGetConvite.loading = false
            state.requestGetConvite.error = false
        },
        setRequestGetConviteLoading: (state: State) => {
            state.requestGetConvite.loading = true
            state.requestGetConvite.error = false
        },
        setRequestGetConviteError: (state: State) => {
            state.requestGetConvite.loading = false
            state.requestGetConvite.error = true
        },
        setRequestGetConviteMessage: (state: State, action: PayloadAction<string>) => {
            state.requestGetConvite.message = action.payload
        },
        resetRequestGetConvite: (state: State) => {
            state.requestGetConvite = {...responseInitialValues}
        },

        setRequestGetConviteCamposData: (state: State, action: PayloadAction<ResponsePattern<CampoDinamico[]>>) => {
            state.requestGetConviteCampos.data = action.payload
            state.requestGetConviteCampos.loading = false
            state.requestGetConviteCampos.error = false
        },
        setRequestGetConviteCamposLoading: (state: State) => {
            state.requestGetConviteCampos.loading = true
            state.requestGetConviteCampos.error = false
        },
        setRequestGetConviteCamposError: (state: State) => {
            state.requestGetConviteCampos.loading = false
            state.requestGetConviteCampos.error = true
        },
        setRequestGetConviteCamposMessage: (state: State, action: PayloadAction<string>) => {
            state.requestGetConviteCampos.message = action.payload
        },
        resetRequestGetConviteCampos: (state: State) => {
            state.requestGetConviteCampos = {...responseInitialValues}
        },

        setRequestSaveConviteData: (state: State, action: PayloadAction<ResponsePattern<any>>) => {
            state.requestSaveConvite.data = action.payload
            state.requestSaveConvite.loading = false
            state.requestSaveConvite.error = false
        },
        setRequestSaveConviteLoading: (state: State) => {
            state.requestSaveConvite.loading = true
            state.requestSaveConvite.error = false
        },
        setRequestSaveConviteError: (state: State) => {
            state.requestSaveConvite.loading = false
            state.requestSaveConvite.error = true
        },
        setRequestSaveConviteMessage: (state: State, action: PayloadAction<string>) => {
            state.requestSaveConvite.message = action.payload
        },
        resetRequestSaveConvite: (state: State) => {
            state.requestSaveConvite = {...responseInitialValues}
        },

        setRequestGetGeoLocalizacaoData: (state: State, action: PayloadAction<ResponsePattern<IGeoLocalizacao>>) => {
            state.requestGetLocalizacao.data = action.payload
            state.requestGetLocalizacao.loading = false
            state.requestGetLocalizacao.error = false
        },
        setRequestGetGeoLocalizacaoLoading: (state: State) => {
            state.requestGetLocalizacao.loading = true
            state.requestGetLocalizacao.error = false
        },
        setRequestGetGeoLocalizacaoError: (state: State) => {
            state.requestGetLocalizacao.loading = false
            state.requestGetLocalizacao.error = true
        },
        setRequestGetGeoLocalizacaoMessage: (state: State, action: PayloadAction<string>) => {
            state.requestGetLocalizacao.message = action.payload
        },
        resetRequestGetGeoLocalizacao: (state: State) => {
            state.requestGetLocalizacao = {...responseInitialValues}
        },

        setRequestGetSessionIdData: (state: State, action: PayloadAction<ResponsePattern<any>>) => {
            state.requestGetSessionId.data = action.payload
            state.requestGetSessionId.loading = false
            state.requestGetSessionId.error = false
        },
        setRequestGetSessionIdLoading: (state: State) => {
            state.requestGetSessionId.loading = true
            state.requestGetSessionId.error = false
        },
        setRequestGetSessionIdError: (state: State) => {
            state.requestGetSessionId.loading = false
            state.requestGetSessionId.error = true
        },
        setRequestGetSessionIdMessage: (state: State, action: PayloadAction<string>) => {
            state.requestGetSessionId.message = action.payload
        },
        resetRequestGetSessionId: (state: State) => {
            state.requestGetSessionId = {...responseInitialValues}
        },

        setRequestValidadeLivenessData: (state: State, action: PayloadAction<ResponsePattern<LivenessResult>>) => {
            state.requestValidadeLiveness.data = action.payload
            state.requestValidadeLiveness.loading = false
            state.requestValidadeLiveness.error = false
        },
        setRequestValidadeLivenessLoading: (state: State) => {
            state.requestValidadeLiveness.loading = true
            state.requestValidadeLiveness.error = false
        },
        setRequestValidadeLivenessError: (state: State) => {
            state.requestValidadeLiveness.loading = false
            state.requestValidadeLiveness.error = true
        },
        setRequestValidadeLivenessMessage: (state: State, action: PayloadAction<string>) => {
            state.requestValidadeLiveness.message = action.payload
        },
        resetRequestValidadeLiveness: (state: State) => {
            state.requestValidadeLiveness = {...responseInitialValues}
        },
    }
})

const actions = requestConviteSlice.actions

export const requestGetConviteReducers = {
    data: actions.setRequestGetConviteData,
    loading: actions.setRequestGetConviteLoading,
    error: actions.setRequestGetConviteError,
    message: actions.setRequestGetConviteMessage,
    reset: actions.resetRequestGetConvite,
}

export const requestGetConviteCamposReducers = {
    data: actions.setRequestGetConviteCamposData,
    loading: actions.setRequestGetConviteCamposLoading,
    error: actions.setRequestGetConviteCamposError,
    message: actions.setRequestGetConviteCamposMessage,
    reset: actions.resetRequestGetConviteCampos,
}

export const requestSaveConviteReducers = {
    data: actions.setRequestSaveConviteData,
    loading: actions.setRequestSaveConviteLoading,
    error: actions.setRequestSaveConviteError,
    message: actions.setRequestSaveConviteMessage,
    reset: actions.resetRequestSaveConvite,
}

export const requestGetGeoLocalizacaoReducers = {
    data: actions.setRequestGetGeoLocalizacaoData,
    loading: actions.setRequestGetGeoLocalizacaoLoading,
    error: actions.setRequestGetGeoLocalizacaoError,
    message: actions.setRequestGetGeoLocalizacaoMessage,
    reset: actions.resetRequestGetGeoLocalizacao,
}

export const requestGetSessionIdReducers = {
    data: actions.setRequestGetSessionIdData,
    loading: actions.setRequestGetSessionIdLoading,
    error: actions.setRequestGetSessionIdError,
    message: actions.setRequestGetSessionIdMessage,
    reset: actions.resetRequestGetSessionId,
}

export const requestValidadeLivenessReducers = {
    data: actions.setRequestValidadeLivenessData,
    loading: actions.setRequestValidadeLivenessLoading,
    error: actions.setRequestValidadeLivenessError,
    message: actions.setRequestValidadeLivenessMessage,
    reset: actions.resetRequestValidadeLiveness,
}

export default requestConviteSlice.reducer